/**
 * Which way the phone is pointing.
 *
 * Navigation that does not rotate with you is a map you have to translate in
 * your head at every corner: the route goes left on screen, you are facing
 * south, and left on screen is behind you. Turning the map so that up is the
 * way you are facing removes that step, which is the whole reason phone
 * navigation looks the way it does.
 *
 * Two sources, in order of preference:
 *
 * 1. The magnetometer, via deviceorientation. True the moment you stand still,
 *    which is exactly when GPS course is useless.
 * 2. GPS course over ground, from the position watch. Only meaningful while
 *    moving, and noisy below walking pace, but it needs no permission and no
 *    magnetometer.
 *
 * iOS wants an explicit grant for the first, and only from inside a user
 * gesture — hence `start()` being something the Start button calls rather than
 * something that happens at load.
 */

/** Smoothing. Raw compass output jitters several degrees at rest. */
const SMOOTHING = 0.25;

/** Below this, GPS course is direction-of-noise rather than direction. */
const MIN_COURSE_SPEED_MS = 0.6;

/** Shortest signed turn from a to b, in degrees. Handles the 360/0 seam. */
export function angleDelta(a, b) {
  return ((((b - a) % 360) + 540) % 360) - 180;
}

/**
 * Blend towards a new heading the short way round.
 *
 * Averaging 350 and 10 naively gives 180 — pointing the map exactly backwards
 * once per revolution.
 */
export function smoothHeading(current, next, weight = SMOOTHING) {
  if (!Number.isFinite(current)) return next;
  return (current + angleDelta(current, next) * weight + 360) % 360;
}

export function createCompass({ onChange } = {}) {
  let heading = null;
  let live = false;
  let listening = false;

  function push(next) {
    if (!Number.isFinite(next)) return;
    const smoothed = smoothHeading(heading, (next + 360) % 360);
    // A tenth of a degree is far below what anyone can see, and repainting the
    // camera for it would fight the easing.
    if (Number.isFinite(heading) && Math.abs(angleDelta(heading, smoothed)) < 0.5) return;
    heading = smoothed;
    onChange?.(heading);
  }

  function onOrientation(event) {
    // Safari reports true north directly and, unlike alpha, already
    // compensated for how the device is held.
    if (Number.isFinite(event.webkitCompassHeading)) {
      live = true;
      return push(event.webkitCompassHeading);
    }
    if (!event.absolute || !Number.isFinite(event.alpha)) return;
    // alpha counts anticlockwise from north; a compass bearing counts
    // clockwise.
    live = true;
    push(360 - event.alpha);
  }

  function listen() {
    if (listening) return;
    listening = true;
    // The absolute event is the one tied to true north. The plain event is a
    // fallback whose zero is wherever the device happened to start, which is
    // useless as a bearing, so it is only read for webkitCompassHeading.
    window.addEventListener("deviceorientationabsolute", onOrientation, true);
    window.addEventListener("deviceorientation", onOrientation, true);
  }

  return {
    /** Last known bearing in degrees clockwise from north, or null. */
    get heading() { return heading; },
    /** Is a real magnetometer feeding this, as opposed to GPS course? */
    get live() { return live; },

    /**
     * Ask for the sensor. Must be called from a user gesture on iOS.
     *
     * Returns whether the sensor is available, but a false is not a failure
     * worth reporting to anyone: GPS course still drives the camera, so
     * navigation works, it just needs you to be moving.
     */
    async start() {
      const request = window.DeviceOrientationEvent?.requestPermission;
      if (typeof request === "function") {
        try {
          if ((await request.call(window.DeviceOrientationEvent)) !== "granted") return false;
        } catch {
          // Refused, or called outside a gesture. Either way, GPS course.
          return false;
        }
      }
      listen();
      return true;
    },

    /**
     * Course from the position watch, used only until the magnetometer speaks.
     *
     * Once a real compass is reporting, this is dropped on the floor — it is
     * the worse of the two signals and switching between them mid-walk reads
     * as the map twitching.
     */
    fromPosition(coords) {
      if (live) return;
      if (!Number.isFinite(coords?.heading)) return;
      if ((coords.speed ?? 0) < MIN_COURSE_SPEED_MS) return;
      push(coords.heading);
    },

    stop() {
      if (!listening) return;
      listening = false;
      window.removeEventListener("deviceorientationabsolute", onOrientation, true);
      window.removeEventListener("deviceorientation", onOrientation, true);
    },
  };
}
